import React, { useEffect, useState } from "react";
import {
  Input,
  Avatar,
  Badge,
  Modal,
  Button,
  Typography,
  List,
  Card,
  Spin, // NEW: spinner
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import {
  createUserGroup,
  fetchUserGroupsByUserId,
  deleteUserGroup,
  updateUserGroup,
} from "../../store/groupSlice";
import "react-toastify/dist/ReactToastify.css";
import ChatDrawer from "./ChatDrawer";
import { fetchGroupMembers } from "../../store/groupMemberSlice";
import { getUserMessages } from "../../store/messageSlice";
const { Text } = Typography;

// Helper to get initials
const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

export default function ChatSidebar({ setSelectedChat }) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { userGroups } = useSelector((state) => state.group);
  const { members } = useSelector((state) => state.groupMembers);
  // grab all incoming messages (private + group) from Redux
  const allMessages = useSelector((state) => state.message.privateMessages);
  // split into private vs. group
  const privateMessages = allMessages.filter((m) => m.receiverId);
  const groupMessages = allMessages.filter((m) => m.groupId);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);

  const currentUserName = userInfo?.username || "You";
  const currentUserAvatar = userInfo?.avatar || null;

  // NEW: grab loading flags from your message slice
  const { loading: messagesLoading } = useSelector((state) => state.message);
  useEffect(() => {
    if (userInfo?.userId) {
      dispatch(fetchUserGroupsByUserId(userInfo.userId));
      dispatch(getUserMessages(userInfo.userId));
    }
  }, [dispatch, userInfo?.userId]);

  // build a map of the very latest msg per receiver
  const lastPrivateMap = privateMessages.reduce((acc, msg) => {
    if (!msg.receiverId) return acc;
    const prev = acc[msg.receiverId];
    if (!prev || new Date(msg.sentAt) > new Date(prev.sentAt)) {
      acc[msg.receiverId] = msg;
    }
    return acc;
  }, {});

  // turn that into an array of chats each with a lastMessage
  const privateChats = Object.values(lastPrivateMap).map((msg) => ({
    id: msg.receiverId,
    name: msg.receiverName,
    lastMessage: { content: msg.content, timestamp: msg.sentAt },
  }));

  // ─── latest per group ───
  const lastGroupMap = groupMessages.reduce((acc, msg) => {
    if (
      !acc[msg.groupId] ||
      new Date(msg.sentAt) > new Date(acc[msg.groupId].sentAt)
    ) {
      acc[msg.groupId] = msg;
    }
    return acc;
  }, {});

  // array of group-chat objects with lastMessage
  const groupChats = userGroups.map((grp) => {
    const lm = lastGroupMap[grp.userGroupID];
    return {
      ...grp,
      lastMessage: lm ? { content: lm.content, timestamp: lm.sentAt } : null,
    };
  });

  const handleCreateGroup = async () => {
    try {
      const groupData = {
        name: newGroupName,
        groupAdminUserID: String(userInfo?.userId),
        createAccess: true,
        replyAccess: true,
      };

      const resultAction = await dispatch(createUserGroup(groupData));

      if (createUserGroup.fulfilled.match(resultAction)) {
        toast.success("Group created successfully!");
        setIsModalOpen(false);
        setNewGroupName("");
        setSelectedMembers([]);
        dispatch(fetchUserGroupsByUserId(userInfo.userId)); // Refresh
      } else {
        throw new Error(resultAction.payload || "Failed to create group");
      }
    } catch (error) {
      toast.error(error.message || "Group creation failed");
    }
  };

  const handleDeleteGroup = async () => {
    if (currentGroup) {
      try {
        const resultAction = await dispatch(
          deleteUserGroup(currentGroup.userGroupID)
        );
        if (deleteUserGroup.fulfilled.match(resultAction)) {
          toast.success("Group deleted successfully!");
          setIsDrawerVisible(false);
          dispatch(fetchUserGroupsByUserId(userInfo.userId)); // Refresh
        } else {
          throw new Error(resultAction.payload || "Failed to delete group");
        }
      } catch (error) {
        toast.error(error.message || "Group deletion failed");
      }
    }
  };

  const handleEditGroupName = async (newName) => {
    if (currentGroup) {
      try {
        const groupData = {
          name: newName,
          groupAdminUserID: String(userInfo?.userId),
          createAccess: true,
          replyAccess: true,
        };
        const resultAction = await dispatch(
          updateUserGroup({ id: currentGroup.userGroupID, groupData })
        );
        if (updateUserGroup.fulfilled.match(resultAction)) {
          toast.success("Group name updated!");
          setIsDrawerVisible(false);
          dispatch(fetchUserGroupsByUserId(userInfo.userId)); // Refresh
        } else {
          throw new Error(
            resultAction.payload || "Failed to update group name"
          );
        }
      } catch (error) {
        toast.error(error.message || "Group name update failed");
      }
    }
  };

  const handleMoreClick = (chat) => {
    setCurrentGroup(chat);
    dispatch(fetchGroupMembers(chat.userGroupID));
    setIsDrawerVisible(true);
  };

  const renderChats = (chatType) => {
    // NEW helper for showing loading
    if (messagesLoading) {
      return (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin size="large" tip="Loading messages..." /> {/* NEW */}
        </div>
      );
    }
    const sourceList = chatType === "group" ? groupChats : privateChats;
    const chatsToRender = sourceList.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <List
        itemLayout="horizontal"
        dataSource={chatsToRender}
        style={{ padding: "0 1rem" }}
        renderItem={(chat) => (
          <List.Item
            onClick={() => setSelectedChat({ ...chat, type: chatType })}
            style={{ cursor: "pointer", borderRadius: 8, margin: "0.25rem 0" }}
            className="hover:shadow-lg transition-shadow"
          >
            <List.Item.Meta
              avatar={
                <Badge count={0} offset={[-5, 5]}>
                  <Avatar size="large">{getInitials(chat.name)}</Avatar>
                </Badge>
              }
              title={
                <Text strong ellipsis style={{ maxWidth: 200 }}>
                  {chat.name}
                </Text>
              }
              description={
                <div>
                  <Text type="secondary" ellipsis style={{ display: "block" }}>
                    {/*NEW display the loading */}
                    {chat.lastMessage
                      ? chat.lastMessage.content
                      : "No messages yet"}{" "}
                  </Text>
                  <div style={{ textAlign: "right" }}>
                    <small className="text-muted">
                      {chat.lastMessage?.timestamp
                        ? new Date(
                            chat.lastMessage.timestamp
                          ).toLocaleTimeString()
                        : "—"}
                    </small>
                  </div>
                </div>
              }
            />
            {chatType === "group" && (
              <MoreOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  handleMoreClick(chat);
                }}
                style={{ fontSize: 18, color: "#888" }}
              />
            )}
          </List.Item>
        )}
      />
    );
  };

  return (
    <aside
      style={{
        background: "url('https://telegram.org/img/bg_pattern_1.png') repeat",
        backdropFilter: "blur(8px)",
        position: "absolute",
        transition: "left 0.3s ease",
      }}
    >
      {/* Header */}
      <div className="px-4 py-4 border-bottom bg-white/90 position-sticky top-0 z-20 d-flex align-items-center justify-between">
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative">
            <Avatar size={48} icon={<UserOutlined />} src={currentUserAvatar} />
            <span
              className="position-absolute top-0 start-100 translate-middle p-1 bg-success border border-white rounded-circle"
              style={{ animation: "pulse 1.5s infinite" }}
            ></span>
          </div>
          <div className="d-flex flex-column">
            <span className="fw-semibold text-dark">{currentUserName}</span>
            <small className="text-success">Online</small>
          </div>
        </div>
      </div>

      {/* Search + New group button */}
      <div className="p-3 border-bottom bg-white/90 z-10 d-flex align-items-center gap-2">
        <Input
          size="large"
          placeholder="Search chats"
          prefix={<SearchOutlined className="text-blue-500" />}
          className="rounded-xl shadow-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      {/* Chat list */}

      <div
        style={{
          padding: 16,
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
        }}
      >
        {/* Private Chats – full width */}
        <Card
          title="Private Chats"
          size="small"
          headStyle={{ fontWeight: "bold", fontSize: "1em" }}
          bodyStyle={{ padding: 0 }}
          style={{ marginBottom: 16 }}
        >
          <List
            itemLayout="horizontal"
            dataSource={privateChats.filter((c) =>
              c.name.toLowerCase().includes(search.toLowerCase())
            )}
            renderItem={(chat) => (
              <List.Item
                onClick={() => setSelectedChat({ ...chat, type: "private" })}
                style={{
                  cursor: "pointer",
                  padding: "12px 16px",
                  transition: "background 0.2s",
                }}
                className="hover:bg-gray-100"
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={0} offset={[-4, 4]}>
                      <Avatar size={40}>{getInitials(chat.name)}</Avatar>
                    </Badge>
                  }
                  title={
                    <Text strong ellipsis style={{ maxWidth: 180 }}>
                      {chat.name}
                    </Text>
                  }
                  description={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text type="secondary" ellipsis style={{ flex: 1 }}>
                        {chat.lastMessage?.content ?? "No messages yet"}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                      >
                        {chat.lastMessage?.timestamp
                          ? new Date(
                              chat.lastMessage.timestamp
                            ).toLocaleTimeString()
                          : "—"}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        {/* Group Chats – full width */}
        <Card
          title="Group Chats"
          size="small"
          headStyle={{ fontWeight: "bold", fontSize: "1em" }}
          bodyStyle={{ padding: 0 }}
        >
          <List
            itemLayout="horizontal"
            dataSource={groupChats.filter((c) =>
              c.name.toLowerCase().includes(search.toLowerCase())
            )}
            renderItem={(chat) => (
              <List.Item
                onClick={() => setSelectedChat({ ...chat, type: "group" })}
                style={{
                  cursor: "pointer",
                  padding: "12px 16px",
                  transition: "background 0.2s",
                }}
                className="hover:bg-gray-100"
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={0} offset={[-4, 4]}>
                      <Avatar size={40}>{getInitials(chat.name)}</Avatar>
                    </Badge>
                  }
                  title={
                    <Text strong ellipsis style={{ maxWidth: 180 }}>
                      {chat.name}
                    </Text>
                  }
                  description={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text type="secondary" ellipsis style={{ flex: 1 }}>
                        {chat.lastMessage?.content ?? "No messages yet"}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                      >
                        {chat.lastMessage?.timestamp
                          ? new Date(
                              chat.lastMessage.timestamp
                            ).toLocaleTimeString()
                          : "—"}
                      </Text>
                    </div>
                  }
                />
                <MoreOutlined
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoreClick(chat);
                  }}
                  style={{ fontSize: 16, color: "#888" }}
                />
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* Create Group Modal */}
      <Modal
        title="Create New Group"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleCreateGroup}
        okText="Create"
        okButtonProps={{
          disabled: !newGroupName,
        }}
      >
        <Input
          placeholder="Group Name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          className="mb-3"
        />
      </Modal>

      <ToastContainer />

      <style>{`.chat-scrollbar-attractive {
          scrollbar-width: thin;
          scrollbar-color: #91caff transparent;
        }

        .chat-scrollbar-attractive::-webkit-scrollbar {
          width: 6px;
        }

        .chat-scrollbar-attractive::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #69b1ff, #91caff);
          border-radius: 8px;
        }

        .chat-scrollbar-attractive::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #4096ff, #69b1ff);
        }

        .chat-scrollbar-attractive::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.6; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <ChatDrawer
        visible={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        group={currentGroup}
        onDeleteGroup={handleDeleteGroup}
        onEditGroupName={handleEditGroupName}
      />
    </aside>
  );
}
